import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Select,
  MenuItem,
  TextField,
  Card,
  CardContent,
  Stack,
  useTheme,
  useMediaQuery,
  Tooltip,
  Divider,
} from "@mui/material";
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import { debounce } from "lodash";
import {
  formatCurrency,
  formatCurrencyInput,
  processCurrencyInput,
  formatCurrencyOnBlur,
  getCurrencyValue,
} from "../../utils/formatters";
import { useTheme as useCustomTheme } from "../../contexts/ThemeContext";

// Add global style for select options
const selectOptionStyle = {
  color: "#000",
  backgroundColor: "#fff",
};

const DebtAnalysisTable = ({
  debtsData = [],
  members = [],
  onSave,
  config = {
    showDates: false,
    editable: true,
    title: "Debt Analysis",
    showActualPayment: true,
    showDebtorName: true,
    showPayoffDate: true,
    showLastUpdated: true,
    showMember: true,
  },
}) => {
  const theme = useTheme();
  const { isDarkMode } = useCustomTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [debts, setDebts] = useState(() => []);  // Initialize as empty array
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showCustomInputs, setShowCustomInputs] = useState({});
  const [isNewRow, setIsNewRow] = useState(false);
  const isLocalEdit = useRef(false);
  const previousDebtsData = useRef(null);

  // Debt types mapping
  const debtTypes = {
    1: "Mortgage",
    2: "Car Note",
    3: "Credit Card",
    4: "Home Equity Line of Credit",
    5: "Student Loan",
    6: "Home Improvement Loan",
    7: "Other Loan",
    8: "Other",
  };

  // Memoize the debt mapping function
  const mapDebt = useCallback((debt) => {
    const formattedDate = debt.updated_at
      ? new Date(debt.updated_at).toISOString().split("T")[0]
      : new Date().toISOString().split("T")[0];

    const memberName = getMemberName(
      debt.user_id,
      debt.owner_type,
      debt.marital_status,
    );

    return {
      ...debt,
      id: debt.id || debt.debt_id || `temp-${Date.now()}`,
      member: memberName,
      memberId: debt.user_id || null,
      ownerType: debt.owner_type || null,
      maritalStatus: debt.marital_status || null,
      debtType: debt.debt_type_id?.toString() || debt.debtType,
      otherDebtType: debt.other_debt_type || debt.otherDebtType || "",
      remainingBalance:
        debt.remaining_balance || debt.remainingBalance || "0.00",
      minimumPayment: debt.minimum_payment || debt.minimumPayment || "0.00",
      actualPayment: debt.actual_payment || debt.actualPayment || "0.00",
      interestRate: debt.interest_rate || debt.interestRate || "0.00",
      startDate: debt.start_date || null,
      endDate: debt.end_date || null,
      debtorName:
        debt.institution_name || debt.debtor_name || debt.debtorName || "",
      payoffDate: debt.payoff_date || debt.payoffDate || null,
      lastUpdated: formattedDate,
      deleted: false,
    };
  }, []);

  // Update debts when debtsData changes and not in local edit
  useEffect(() => {
    if (isLocalEdit.current) return;

    // Check if we've already processed this exact data
    const currentDataString = JSON.stringify(debtsData);
    if (previousDebtsData.current === currentDataString) {
      // console.log('Skipping duplicate data processing');
      return;
    }
    previousDebtsData.current = currentDataString;

    // First, deduplicate the debts by payment amounts AND information completeness
    const debtMap = new Map();
    debtsData.forEach((debt) => {
      // Create a composite key from both payment amounts
      const key = `${debt.minimum_payment || '0'}_${debt.actual_payment || '0'}`;
      
      // Calculate how complete this debt's information is
      const completenessScore = [
        debt.institution_name || debt.debtor_name || debt.debtorName,
        debt.debt_type_id,
        debt.interest_rate,
        debt.remaining_balance,
        debt.payoff_date
      ].filter(Boolean).length;

      const existing = debtMap.get(key);
      const existingScore = existing ? [
        existing.institution_name || existing.debtor_name || existing.debtorName,
        existing.debt_type_id,
        existing.interest_rate,
        existing.remaining_balance,
        existing.payoff_date
      ].filter(Boolean).length : -1;

      // Replace if:
      // 1. No existing debt for this amount combo, OR
      // 2. This debt has more complete information
      if (!existing || completenessScore > existingScore) {
        debtMap.set(key, debt);
      }
    });

    // Map the deduplicated debts using memoized function
    const mappedDebts = Array.from(debtMap.values()).map(mapDebt);

    // console.log('Setting new debts:', mappedDebts);
    setDebts(mappedDebts);
  }, [debtsData, members, mapDebt]);

  // Toggle custom input fields
  const toggleCustomInput = (id, field, value) => {
    setShowCustomInputs((prev) => ({
      ...prev,
      [id]: {
        ...(prev[id] || {}),
        [field]: value,
      },
    }));
  };

  // Handle input changes
  const handleInputChange = (id, field, value) => {
    isLocalEdit.current = true;

    setDebts((prev) =>
      prev.map((debt) => (debt.id === id ? { ...debt, [field]: value } : debt)),
    );

    debouncedSave();
  };

  // Debounced save function
  const debouncedSave = debounce(() => {
    onSave(debts.filter((debt) => !debt.deleted));
    isLocalEdit.current = false;
  }, 500);

  // Start editing a debt entry
  const handleEdit = (debt) => {
    setEditingId(debt.id);
    setEditForm({
      ...debt,
    });
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    if (isNewRow) {
      // If this is a new row being added, remove it entirely
      const updatedDebts = debts.filter((debt) => debt.id !== editingId);
      setDebts(updatedDebts);
    }

    // Reset editing state
    setEditingId(null);
    setIsNewRow(false);
  };

  // Save changes
  const handleSaveEdit = () => {
    isLocalEdit.current = true;

    // Process the form data
    const processedEditForm = { ...editForm };

    // Convert amount from formatted to numeric if needed
    if (processedEditForm.remainingBalanceFormatted) {
      processedEditForm.remainingBalance = getCurrencyValue(
        processedEditForm.remainingBalanceFormatted,
      );
      delete processedEditForm.remainingBalanceFormatted;
    }

    if (processedEditForm.minimumPaymentFormatted) {
      processedEditForm.minimumPayment = getCurrencyValue(
        processedEditForm.minimumPaymentFormatted,
      );
      delete processedEditForm.minimumPaymentFormatted;
    }

    if (processedEditForm.actualPaymentFormatted) {
      processedEditForm.actualPayment = getCurrencyValue(
        processedEditForm.actualPaymentFormatted,
      );
      delete processedEditForm.actualPaymentFormatted;
    }

    // Process interest rate to ensure it's just a number
    if (processedEditForm.interestRate) {
      processedEditForm.interestRate = processedEditForm.interestRate
        .toString()
        .replace(/[^0-9.]/g, "");
    }

    // No need to set lastUpdated manually as it will be handled by the database's updated_at field

    const updatedData = debts.map((item) =>
      item.id === editingId
        ? {
            ...processedEditForm,
            // Ensure we keep the original data formats that might be expected elsewhere
            member: processedEditForm.member,
            member_id: processedEditForm.memberId, // For backward compatibility
            user_id: processedEditForm.memberId, // For the new database schema
            owner_type: processedEditForm.ownerType, // Preserve owner type
            marital_status: processedEditForm.maritalStatus, // Preserve marital status
            debt_type_id: processedEditForm.debtType,
            remaining_balance: processedEditForm.remainingBalance,
            minimum_payment: processedEditForm.minimumPayment,
            actual_payment: processedEditForm.actualPayment,
            interest_rate: processedEditForm.interestRate,
            other_debt_type: processedEditForm.otherDebtType,
            debtor_name: processedEditForm.debtorName,
            payoff_date: processedEditForm.payoffDate,
            // Remove last_updated mapping to rely on the database timestamp
          }
        : item,
    );

    setDebts(updatedData);
    onSave(updatedData.filter((debt) => !debt.deleted));
    setEditingId(null);
    setIsNewRow(false);
    isLocalEdit.current = false;
  };

  // Add new debt
  const handleAdd = () => {
    const newId = `temp-${Date.now()}`;

    // Default to first debt type
    const defaultDebtType = "5"; // Credit Card

    // Create new debt
    const newDebt = {
      id: newId,
      member: "Household/Joint",
      memberId: null,
      debtType: defaultDebtType,
      remainingBalance: 0,
      remainingBalanceFormatted: "$0.00",
      minimumPayment: 0,
      minimumPaymentFormatted: "$0.00",
      actualPayment: 0,
      actualPaymentFormatted: "$0.00",
      interestRate: "",
      otherDebtType: "",
      debtorName: "",
      startDate: config.showDates
        ? new Date().toISOString().split("T")[0]
        : null,
      endDate: config.showDates
        ? new Date(new Date().getFullYear(), 11, 31).toISOString().split("T")[0]
        : null,
      payoffDate: config.showPayoffDate
        ? new Date(new Date().getFullYear() + 5, 11, 31)
            .toISOString()
            .split("T")[0]
        : null,
      lastUpdated: new Date().toISOString().split("T")[0],
      deleted: false,
    };

    setDebts([...debts, newDebt]);
    setIsNewRow(true);
    handleEdit(newDebt);
    toggleCustomInput(newId, "debtType", false);
  };

  // Delete debt
  const handleDelete = (id) => {
    isLocalEdit.current = true;

    const updatedData = debts.map((item) =>
      item.id === id ? { ...item, deleted: true } : item,
    );

    setDebts(updatedData);
    onSave(updatedData.filter((debt) => !debt.deleted));
    isLocalEdit.current = false;
  };

  // Get debt type name from debtTypes mapping
  const getDebtTypeName = (typeId) => {
    if (!typeId) return "Unknown";
    return debtTypes[typeId] || "Unknown";
  };

  // Format percentage
  const formatAsPercentage = (value) => {
    if (!value) return "";
    const numericValue =
      parseFloat(value.toString().replace(/[^0-9.]/g, "")) || 0;
    return `${numericValue}%`;
  };

  // Memoize formatAsCurrency function
  const formatAsCurrency = useCallback((value) => {
    if (!value) return "$0.00";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  }, []);

  // Memoize display functions
  const displayMinimumPayment = useCallback(
    (payment) => {
      return formatAsCurrency(payment);
    },
    [formatAsCurrency],
  );

  const displayActualPayment = useCallback(
    (payment) => {
      return formatAsCurrency(payment);
    },
    [formatAsCurrency],
  );

  // Memoize processed debts
  const processedDebts = useMemo(() => {
    return debts
      .filter((debt) => !debt.deleted)
      .map((debt) => ({
        ...debt,
        minimumPayment: displayMinimumPayment(debt.minimum_payment),
        actualPayment: displayActualPayment(debt.actual_payment),
        remainingBalance: formatAsCurrency(debt.remaining_balance),
      }));
  }, [debts, displayMinimumPayment, displayActualPayment, formatAsCurrency]);

  // Calculate total liabilities
  const calculateTotalLiabilities = () => {
    return debts
      .filter((debt) => !debt.deleted)
      .reduce(
        (total, debt) =>
          total +
            parseFloat(
              String(debt.remainingBalance || 0).replace(/[$,]/g, ""),
            ) || 0,
        0,
      );
  };

  // Calculate total minimum payments
  const calculateTotalMinimumPayments = () => {
    return debts
      .filter((debt) => !debt.deleted)
      .reduce(
        (total, debt) =>
          total +
            parseFloat(String(debt.minimumPayment || 0).replace(/[$,]/g, "")) ||
          0,
        0,
      );
  };

  // Find member name from ID
  const getMemberName = (memberId, ownerTypeId, maritalStatus) => {
    // If we have a direct member ID match, use that
    if (memberId) {
      const member = members.find(
        (m) =>
          m.userId?.toString() === memberId.toString() ||
          m.user_id?.toString() === memberId.toString(),
      );
      if (member) {
        return `${member.firstName || ""} ${member.lastName || ""}`.trim();
      }
    }

    // If we have an owner type ID, try to map it to a member
    if (ownerTypeId && members && members.length > 0) {
      // Find form submitter (primary) and spouse (secondary)
      const primaryMember = members.find((m) => m.isFormSubmitter === true);
      const spouseMember = members.find((m) => m.isFormSubmitter !== true);

      // For single person, use the primary member's name
      if (maritalStatus === "no" && primaryMember) {
        return `${primaryMember.firstName || ""} ${primaryMember.lastName || ""}`.trim();
      }

      // For married, map owner type to appropriate member
      if (maritalStatus === "yes") {
        if (ownerTypeId === 1 && primaryMember) {
          // Self
          return `${primaryMember.firstName || ""} ${primaryMember.lastName || ""}`.trim();
        } else if (ownerTypeId === 2 && spouseMember) {
          // Spouse
          return `${spouseMember.firstName || ""} ${spouseMember.lastName || ""}`.trim();
        } else if (ownerTypeId === 3) {
          // Joint
          return "Joint";
        }
      }
    }

    // Default to Joint for married or when we can't determine
    return maritalStatus === "no"
      ? members[0]?.firstName + " " + members[0]?.lastName
      : "Joint";
  };

  // Memoize the rendered rows to prevent unnecessary re-renders
  const renderedRows = useMemo(() => {
    return debts
      .filter((debt) => !debt.deleted)
      .map((debt) => {
        const uniqueKey = debt.id || `debt-${debt.debtorName}-${debt.debtType}-${Date.now()}`;
        return (
          <TableRow key={uniqueKey} className="even:bg-gray-50 hover:bg-gray-100 dark:hover:bg-gray-800">
            {editingId === debt.id ? (
              // Editing row
              <>
                {config.showMember && (
                  <TableCell>
                    <Select
                      value={editForm.memberId || ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === "joint") {
                          // Set joint ownership
                          setEditForm({
                            ...editForm,
                            memberId: null,
                            ownerType: 3, // Joint owner type
                            member: "Joint",
                          });
                        } else if (value === "self") {
                          // Set self ownership
                          const primaryMember = members.find(
                            (m) => m.isFormSubmitter === true,
                          );
                          setEditForm({
                            ...editForm,
                            memberId:
                              primaryMember?.userId || null,
                            ownerType: 1, // Self owner type
                            member: primaryMember
                              ? `${primaryMember.firstName} ${primaryMember.lastName}`
                              : "Self",
                          });
                        } else if (value === "spouse") {
                          // Set spouse ownership
                          const spouseMember = members.find(
                            (m) => m.isFormSubmitter !== true,
                          );
                          setEditForm({
                            ...editForm,
                            memberId:
                              spouseMember?.userId || null,
                            ownerType: 2, // Spouse owner type
                            member: spouseMember
                              ? `${spouseMember.firstName} ${spouseMember.lastName}`
                              : "Spouse",
                          });
                        }
                      }}
                      className="w-full border rounded-md p-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 dark:text-gray-100 bg-surface-default dark:bg-surface-default"
                    >
                      <MenuItem
                        value=""
                        className="text-gray-900 dark:text-gray-100 bg-surface-default dark:bg-surface-default"
                        style={selectOptionStyle}
                      >
                        Select Owner
                      </MenuItem>
                      <MenuItem
                        value="joint"
                        className="text-gray-900 dark:text-gray-100 bg-surface-default dark:bg-surface-default"
                        style={selectOptionStyle}
                      >
                        Joint
                      </MenuItem>
                      <MenuItem
                        value="self"
                        className="text-gray-900 dark:text-gray-100 bg-surface-default dark:bg-surface-default"
                        style={selectOptionStyle}
                      >
                        Self
                      </MenuItem>
                      <MenuItem
                        value="spouse"
                        className="text-gray-900 dark:text-gray-100 bg-surface-default dark:bg-surface-default"
                        style={selectOptionStyle}
                      >
                        Spouse
                      </MenuItem>
                    </Select>
                  </TableCell>
                )}
                {config.showDebtorName && (
                  <TableCell>
                    <TextField
                      type="text"
                      value={editForm.debtorName || ""}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          debtorName: e.target.value,
                        })
                      }
                      className="w-full border rounded-md p-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Debtor/Lender name"
                    />
                  </TableCell>
                )}
                <TableCell>
                  {!showCustomInputs[debt.id]?.debtType ? (
                    <Select
                      value={editForm.debtType || ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === "8") {
                          toggleCustomInput(
                            debt.id,
                            "debtType",
                            true,
                          );
                        }
                        setEditForm({
                          ...editForm,
                          debtType: value,
                        });
                      }}
                      className="w-full border rounded-md p-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 dark:text-gray-100 bg-surface-default dark:bg-surface-default"
                    >
                      <MenuItem
                        value=""
                        className="text-gray-900 dark:text-gray-100 bg-surface-default dark:bg-surface-default"
                        style={selectOptionStyle}
                      >
                        Select Type
                      </MenuItem>
                      {Object.entries(debtTypes).map(
                        ([id, name]) => (
                          <MenuItem
                            key={id}
                            value={id}
                            className="text-gray-900 dark:text-gray-100 bg-surface-default dark:bg-surface-default"
                            style={selectOptionStyle}
                          >
                            {name}
                          </MenuItem>
                        ),
                      )}
                    </Select>
                  ) : (
                    <TextField
                      type="text"
                      value={editForm.otherDebtType || ""}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          otherDebtType: e.target.value,
                        })
                      }
                      className="w-full border rounded-md p-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Custom debt type"
                    />
                  )}
                </TableCell>
                <TableCell>
                  <TextField
                    type="text"
                    value={
                      editForm.minimumPaymentFormatted ||
                      formatCurrencyInput(
                        editForm.minimumPayment || 0,
                      )
                    }
                    onChange={(e) => {
                      const rawValue = e.target.value;
                      const processedValue =
                        processCurrencyInput(rawValue);
                      setEditForm({
                        ...editForm,
                        minimumPayment: processedValue,
                        minimumPaymentFormatted:
                          formatCurrencyInput(processedValue),
                      });
                    }}
                    onBlur={(e) => {
                      const formattedValue = formatCurrencyOnBlur(
                        editForm.minimumPayment,
                      );
                      const numericValue =
                        getCurrencyValue(formattedValue);
                      setEditForm({
                        ...editForm,
                        minimumPayment: String(numericValue),
                        minimumPaymentFormatted: formattedValue,
                      });
                    }}
                    onFocus={(e) => {
                      if (e.target.value === "$0.00") {
                        e.target.select();
                      }
                    }}
                    className="w-full border rounded-md p-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Minimum Payment"
                  />
                </TableCell>
                {config.showActualPayment && (
                  <TableCell>
                    <TextField
                      type="text"
                      value={
                        editForm.actualPaymentFormatted ||
                        formatCurrencyInput(
                          editForm.actualPayment || 0,
                        )
                      }
                      onChange={(e) => {
                        const rawValue = e.target.value;
                        const processedValue =
                          processCurrencyInput(rawValue);
                        setEditForm({
                          ...editForm,
                          actualPayment: processedValue,
                          actualPaymentFormatted:
                            formatCurrencyInput(processedValue),
                        });
                      }}
                      onBlur={(e) => {
                        const formattedValue =
                          formatCurrencyOnBlur(
                            editForm.actualPayment,
                          );
                        const numericValue =
                          getCurrencyValue(formattedValue);
                        setEditForm({
                          ...editForm,
                          actualPayment: String(numericValue),
                          actualPaymentFormatted: formattedValue,
                        });
                      }}
                      onFocus={(e) => {
                        if (e.target.value === "$0.00") {
                          e.target.select();
                        }
                      }}
                      className="w-full border rounded-md p-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Actual Payment"
                    />
                  </TableCell>
                )}
                <TableCell>
                  <TextField
                    type="text"
                    value={
                      editForm.remainingBalanceFormatted ||
                      formatCurrencyInput(
                        editForm.remainingBalance || 0,
                      )
                    }
                    onChange={(e) => {
                      // Process the input to allow only valid currency characters
                      const rawValue = e.target.value;
                      // Remove all formatting characters ($, commas)
                      const processedValue =
                        processCurrencyInput(rawValue);
                      // Store both the numeric and formatted value
                      setEditForm({
                        ...editForm,
                        remainingBalance: processedValue,
                        remainingBalanceFormatted:
                          formatCurrencyInput(processedValue),
                      });
                    }}
                    onBlur={(e) => {
                      // When field loses focus, ensure proper formatting
                      const formattedValue = formatCurrencyOnBlur(
                        editForm.remainingBalance,
                      );
                      const numericValue =
                        getCurrencyValue(formattedValue);
                      setEditForm({
                        ...editForm,
                        remainingBalance: String(numericValue),
                        remainingBalanceFormatted: formattedValue,
                      });
                    }}
                    onFocus={(e) => {
                      // If the field gets focus and has a default value, select all text
                      if (e.target.value === "$0.00") {
                        e.target.select();
                      }
                    }}
                    className="w-full border rounded-md p-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Amount"
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    type="text"
                    value={editForm.interestRate || ""}
                    onChange={(e) => {
                      const value = e.target.value.replace(
                        /[^0-9.]/g,
                        "",
                      );
                      setEditForm({
                        ...editForm,
                        interestRate: value,
                      });
                    }}
                    onBlur={(e) => {
                      if (e.target.value) {
                        setEditForm({
                          ...editForm,
                          interestRate: formatAsPercentage(
                            e.target.value,
                          ),
                        });
                      }
                    }}
                    className="w-full border rounded-md p-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="0.00%"
                  />
                </TableCell>
                {config.showDates && (
                  <>
                    <TableCell>
                      <TextField
                        type="date"
                        value={editForm.startDate || ""}
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            startDate: e.target.value,
                          })
                        }
                        className="w-full border rounded-md p-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="date"
                        value={editForm.endDate || ""}
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            endDate: e.target.value,
                          })
                        }
                        className="w-full border rounded-md p-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </TableCell>
                  </>
                )}
                {config.showPayoffDate && (
                  <TableCell>
                    <TextField
                      type="date"
                      value={editForm.payoffDate || ""}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          payoffDate: e.target.value,
                        })
                      }
                      className="w-full border rounded-md p-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </TableCell>
                )}
                {config.showLastUpdated && (
                  <TableCell>
                    <TextField
                      type="date"
                      value={
                        editForm.lastUpdated ||
                        new Date().toISOString().split("T")[0]
                      }
                      className="w-full border rounded-md p-2 bg-gray-100 text-gray-700 dark:text-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 cursor-not-allowed"
                      disabled
                      title="This field is automatically updated when changes are saved"
                    />
                    <small className="text-gray-500 dark:text-gray-500 mt-1 block">
                      Auto-updated on save
                    </small>
                  </TableCell>
                )}
                {config.editable && (
                  <TableCell align="right">
                    <IconButton
                      onClick={handleSaveEdit}
                      color="success"
                      aria-label="Save"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      onClick={handleCancelEdit}
                      color="error"
                      aria-label="Cancel"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                )}
              </>
            ) : (
              // Display row
              <>
                {config.showMember && (
                  <TableCell sx={{ color: isDarkMode ? 'text.primary' : 'inherit' }}>
                    {debt.member || "Household/Joint"}
                  </TableCell>
                )}
                {config.showDebtorName && (
                  <TableCell sx={{ color: isDarkMode ? 'text.primary' : 'inherit' }}>
                    {debt.debtorName || ""}
                  </TableCell>
                )}
                <TableCell sx={{ color: isDarkMode ? 'text.primary' : 'inherit' }}>
                  {debt.debtType === "8" && debt.otherDebtType
                    ? debt.otherDebtType
                    : getDebtTypeName(debt.debtType)}
                </TableCell>
                <TableCell sx={{ color: isDarkMode ? 'text.primary' : 'inherit' }}>
                  {displayMinimumPayment(debt.minimum_payment)}
                </TableCell>
                {config.showActualPayment && (
                  <TableCell sx={{ color: isDarkMode ? 'text.primary' : 'inherit' }}>
                    {displayActualPayment(debt.actual_payment)}
                  </TableCell>
                )}
                <TableCell sx={{ color: isDarkMode ? 'text.primary' : 'inherit' }}>
                  {debt.remainingBalance}
                </TableCell>
                <TableCell sx={{ color: isDarkMode ? 'text.primary' : 'inherit' }}>
                  {formatAsPercentage(debt.interestRate)}
                </TableCell>
                {config.showDates && (
                  <>
                    <TableCell sx={{ color: isDarkMode ? 'text.primary' : 'inherit' }}>
                      {debt.startDate}
                    </TableCell>
                    <TableCell sx={{ color: isDarkMode ? 'text.primary' : 'inherit' }}>
                      {debt.endDate}
                    </TableCell>
                  </>
                )}
                {config.showPayoffDate && (
                  <TableCell sx={{ color: isDarkMode ? 'text.primary' : 'inherit' }}>
                    {debt.payoffDate}
                  </TableCell>
                )}
                {config.showLastUpdated && (
                  <TableCell sx={{ color: isDarkMode ? 'text.primary' : 'inherit' }}>
                    <div className="font-medium">
                      {debt.lastUpdated}
                    </div>
                    <div className={isDarkMode ? 'text-gray-400' : 'text-gray-500 dark:text-gray-500'}>
                      Auto-updated on save
                    </div>
                  </TableCell>
                )}
                {config.editable && (
                  <TableCell align="right">
                    <IconButton
                      onClick={() => handleEdit(debt)}
                      color="primary"
                      aria-label="Edit"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      onClick={() => handleDelete(debt.id)}
                      color="error"
                      aria-label="Delete"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                )}
              </>
            )}
          </TableRow>
        );
      });
  }, [debts, editingId, editForm]);

  return (
    <Box sx={{ width: "100%" }}>
      <Card 
        elevation={2} 
        sx={{ 
          mb: 4, 
          borderRadius: 2,
          bgcolor: isDarkMode ? 'background.paper' : 'background.default',
          color: isDarkMode ? 'text.primary' : 'inherit',
          border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.12)' : 'none'
        }}
      >
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 3,
            }}
          >
            {config.title && (
              <Typography 
                variant="h5" 
                component="h2" 
                fontWeight="600"
                sx={{ color: isDarkMode ? 'text.primary' : 'inherit' }}
              >
                {config.title}
              </Typography>
            )}
            {config.editable && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAdd}
                sx={{
                  borderRadius: 20,
                  background: isDarkMode 
                    ? "linear-gradient(45deg, #1565c0 30%, #1976d2 90%)"
                    : "linear-gradient(45deg, #1976d2 30%, #2196f3 90%)",
                  boxShadow: isDarkMode
                    ? "0 3px 5px 2px rgba(25, 118, 210, .5)"
                    : "0 3px 5px 2px rgba(33, 150, 243, .3)",
                  "&:hover": {
                    background: isDarkMode
                      ? "linear-gradient(45deg, #0d47a1 30%, #1565c0 90%)"
                      : "linear-gradient(45deg, #1565c0 30%, #1976d2 90%)",
                    transform: "translateY(-1px)",
                  },
                  transition: "all 0.2s",
                }}
              >
                Add Debt
              </Button>
            )}
          </Box>

          <Paper
            elevation={1}
            sx={{ 
              borderRadius: 2, 
              overflow: "hidden", 
              mb: 3,
              bgcolor: isDarkMode ? 'background.paper' : 'background.default',
              border: isDarkMode ? '1px solid rgba(255, 255, 255, 0.12)' : 'none'
            }}
          >
            <TableContainer>
              <Table size={isMobile ? "small" : "medium"}>
                <TableHead>
                  <TableRow>
                    {config.showMember && (
                      <TableCell sx={{ color: isDarkMode ? 'text.primary' : 'inherit' }}>
                        Member
                      </TableCell>
                    )}
                    {config.showDebtorName && (
                      <TableCell sx={{ color: isDarkMode ? 'text.primary' : 'inherit' }}>
                        Debtor/Lender
                      </TableCell>
                    )}
                    <TableCell sx={{ color: isDarkMode ? 'text.primary' : 'inherit' }}>Type</TableCell>
                    <TableCell sx={{ color: isDarkMode ? 'text.primary' : 'inherit' }}>Minimum Payment</TableCell>
                    {config.showActualPayment && (
                      <TableCell sx={{ color: isDarkMode ? 'text.primary' : 'inherit' }}>Actual Payment</TableCell>
                    )}
                    <TableCell sx={{ color: isDarkMode ? 'text.primary' : 'inherit' }}>Remaining Balance</TableCell>
                    <TableCell sx={{ color: isDarkMode ? 'text.primary' : 'inherit' }}>Interest Rate</TableCell>
                    {config.showDates && (
                      <>
                        <TableCell sx={{ color: isDarkMode ? 'text.primary' : 'inherit' }}>Start Date</TableCell>
                        <TableCell sx={{ color: isDarkMode ? 'text.primary' : 'inherit' }}>End Date</TableCell>
                      </>
                    )}
                    {config.showPayoffDate && (
                      <TableCell sx={{ color: isDarkMode ? 'text.primary' : 'inherit' }}>Payoff Date</TableCell>
                    )}
                    {config.showLastUpdated && (
                      <TableCell sx={{ color: isDarkMode ? 'text.primary' : 'inherit' }}>Last Update</TableCell>
                    )}
                    {config.editable && (
                      <TableCell sx={{ color: isDarkMode ? 'text.primary' : 'inherit' }} align="right">Actions</TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {renderedRows}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          {/* Total Liabilities Summary Card */}
          <Card
            sx={{
              p: 2,
              bgcolor: isDarkMode ? 'primary.dark' : theme.palette.primary.main,
              color: "white",
              borderRadius: 2,
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
            >
              <Typography variant="h6">Total Liabilities</Typography>
              <Typography variant="h5" fontWeight="600">
                {formatAsCurrency(calculateTotalLiabilities())}
              </Typography>
            </Stack>
          </Card>
        </CardContent>
      </Card>
    </Box>
  );
};

export default DebtAnalysisTable;
